import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { getSeasonValidationStatus, type SeasonValidationStatus } from '@/client/admin.client';

export function SeasonValidationPage() {
  const [seasonIdInput, setSeasonIdInput] = useState('');
  const [data, setData] = useState<SeasonValidationStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleLoad() {
    const seasonId = Number(seasonIdInput);
    if (!Number.isInteger(seasonId) || seasonId <= 0) {
      setError('Season ID must be a positive integer.');
      return;
    }
    setLoading(true);
    setError(null);
    setData(null);
    try {
      const result = await getSeasonValidationStatus(seasonId);
      setData(result);
    } catch (e) {
      setError((e as Error).message ?? String(e));
    } finally {
      setLoading(false);
    }
  }

  const gatePassed = data != null && data.summary.pending === 0;

  return (
    <div className="space-y-4 p-4">
      <h1 className="text-xl font-semibold">Season Validation Status</h1>
      <div className="flex items-center gap-3">
        <input
          type="number"
          min={1}
          placeholder="Season ID"
          value={seasonIdInput}
          onChange={(e) => setSeasonIdInput(e.target.value)}
          className="border rounded px-2 py-1 w-36"
        />
        <Button
          size="sm"
          disabled={loading || seasonIdInput === ''}
          onClick={() => void handleLoad()}
        >
          Load
        </Button>
        {loading && <span className="text-sm text-muted-foreground">loading…</span>}
      </div>
      {error && <div className="text-sm text-red-500">{error}</div>}
      {data && (
        <>
          <div className="flex flex-wrap gap-4 rounded border p-3 text-sm">
            <span>
              <span className="font-medium">Total ranked:</span> {data.summary.totalRanked}
            </span>
            <span>
              <span className="font-medium">Validated:</span> {data.summary.validated}
            </span>
            <span>
              <span className="font-medium">Pending:</span> {data.summary.pending}
            </span>
            <span
              className={gatePassed ? 'font-semibold text-green-600' : 'font-semibold text-red-500'}
            >
              Snapshot gate: {gatePassed ? 'PASSED' : 'BLOCKED'}
            </span>
          </div>
          <div className="text-sm text-muted-foreground">{data.rankings.length} rows</div>
          <div className="max-h-[65vh] overflow-auto border rounded">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>#</TableHead>
                  <TableHead>Account</TableHead>
                  <TableHead>Score</TableHead>
                  <TableHead>Validated</TableHead>
                  <TableHead>Flagged</TableHead>
                  <TableHead>Banned</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.rankings.map((row, i) => (
                  <TableRow key={row.account}>
                    <TableCell className="font-mono text-xs">{i + 1}</TableCell>
                    <TableCell className="font-mono text-xs break-all">{row.account}</TableCell>
                    <TableCell className="font-mono text-xs">{row.score}</TableCell>
                    <TableCell className="text-xs">{row.validated ? '✓' : '–'}</TableCell>
                    <TableCell className="text-xs">
                      {row.flagged ? (
                        <span className="text-amber-600 font-medium">⚑ flagged</span>
                      ) : (
                        '–'
                      )}
                    </TableCell>
                    <TableCell className="text-xs">
                      {row.banned ? (
                        <span className="text-red-600 font-medium">✕ banned</span>
                      ) : (
                        '–'
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </>
      )}
    </div>
  );
}

export default SeasonValidationPage;
